import React, { useState, Fragment, forwardRef, useContext, useEffect, useMemo } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import LocationList from "./LocationList";
import Circle from "@uiw/react-color-circle";
import Popover from "@material-ui/core/Popover";
import Button from "@material-ui/core/Button";
import { deleteLocationGroup, deleteLocationsByGroup, updateLocationGroup } from "../util/db";
import { Collapse } from "react-collapse";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import MoreHoriz from "@material-ui/icons/MoreHoriz";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import NotesIcon from "@material-ui/icons/Notes";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { useForm } from "react-hook-form";
import CircularProgress from "@material-ui/core/CircularProgress";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import DatePicker from "react-datepicker";
import { copyLocationGroup, deletePhoneFromLocationGroup, deleteSpecialInstructionsFromLocationGroup, deleteWebsiteLinkFromLocationGroup } from "../util/db";
import { DateContext } from "../providers/DateProvider";
import { CategoryContext } from "../providers/CategoryProvider";
import AddIcCallIcon from "@material-ui/icons/AddIcCall";
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-hot-toast";
import styled, { createGlobalStyle } from "styled-components";
import { Timestamp } from "firebase/firestore";

const useStyles = makeStyles(() => ({
  groupContainer: { marginBottom: -10 },
  groupBoxHeader: {
    paddingTop: 10,
    paddingBottom: 10,
    fontSize: 16,
    fontWeight: 800,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  groupHeader: {
    paddingTop: 10,
    paddingBottom: 10,
    fontSize: 16,
    fontWeight: 800,
  },
}));

// ---------- helpers (dayKey-first) ----------
const pad2 = (n) => String(n).padStart(2, "0");
const toDayKey = (dateLike) => {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// Parse "YYYY-MM-DD" into a Date at LOCAL start-of-day
const dayKeyToLocalDate = (dayKey) => {
  const [y, m, d] = dayKey.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const startOfLocalDay = (dateLike) => {
  const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Format dayKey (or Date) to MM/DD(/YY)
const formatMMDD = (dateLike, shortYear = false) => {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  return shortYear ? `${mm}/${dd}/${String(yyyy).slice(-2)}` : `${mm}/${dd}/${yyyy}`;
};

const DatePickerStyles = createGlobalStyle`
  .group-copy-popper {
    z-index: 1700 !important;
  }

  .group-copy-datepicker.react-datepicker,
  .group-range-datepicker.react-datepicker {
    background-color: #151824ff;
    border-radius: 5px;
    padding: 10px;
    border-color: #151824ff;
    min-height: 450px;
    color: white;
  }

  .group-copy-datepicker .react-datepicker__month-container,
  .group-range-datepicker .react-datepicker__month-container {
    background-color: #151824ff;
    min-height: 450px;
  }

  .group-range-datepicker.react-datepicker {
    min-height: auto;
    padding-bottom: 10px;
  }

  .group-range-datepicker .react-datepicker__month-container {
    min-height: auto;
  }

  .group-copy-datepicker .react-datepicker__day,
  .group-range-datepicker .react-datepicker__day {
    color: white;
    line-height: 2.5rem;
  }

  .group-copy-datepicker .react-datepicker__day:hover,
  .group-range-datepicker .react-datepicker__day:hover {
    background-color: #0059a6;
  }

  .group-copy-datepicker .react-datepicker__day--keyboard-selected,
  .group-range-datepicker .react-datepicker__day--keyboard-selected {
    background: none;
  }

  .group-copy-datepicker .react-datepicker__day--selected,
  .group-copy-datepicker .react-datepicker__day--in-selecting-range,
  .group-copy-datepicker .react-datepicker__day--in-range,
  .group-range-datepicker .react-datepicker__day--selected,
  .group-range-datepicker .react-datepicker__day--in-selecting-range,
  .group-range-datepicker .react-datepicker__day--in-range,
  .group-range-datepicker .react-datepicker__day--range-start,
  .group-range-datepicker .react-datepicker__day--range-end {
    background-color: #e91e63;
    color: white;
  }

  .group-copy-datepicker .react-datepicker__header,
  .group-range-datepicker .react-datepicker__header {
    background-color: #151824ff;
    border-bottom: 1px solid #0059a6;
  }

  .group-copy-datepicker .react-datepicker__header {
    padding-top: 40px;
  }

  .group-copy-datepicker .react-datepicker__month {
    margin-top: 10px;
  }

  .group-copy-datepicker .react-datepicker__navigation {
    top: 52px;
  }

  .group-copy-datepicker .react-datepicker__current-month,
  .group-copy-datepicker .react-datepicker__day-name,
  .group-range-datepicker .react-datepicker__current-month,
  .group-range-datepicker .react-datepicker__day-name {
    color: white;
  }

  .group-copy-datepicker .react-datepicker__today-button,
  .group-range-datepicker .react-datepicker__today-button {
    background-color: #0059a6;
    color: white;
    padding: 10px;
    border-radius: 5px;
    border: none;
    position: absolute;
    bottom: 10px;
    width: 95%;
  }

  .group-copy-datepicker .react-datepicker__day--selected:hover,
  .group-range-datepicker .react-datepicker__day--selected:hover {
    background-color: #e91e63;
  }

  .group-range-datepicker .react-datepicker__navigation {
    top: 20px;
  }

  .group-copy-datepicker .react-datepicker__header__dropdown,
  .group-range-datepicker .react-datepicker__header__dropdown {
    padding-top: 10px;
  }

  .group-copy-datepicker .react-datepicker__month-select,
  .group-copy-datepicker .react-datepicker__year-select,
  .group-range-datepicker .react-datepicker__month-select,
  .group-range-datepicker .react-datepicker__year-select {
    color: white;
    background-color: #151824ff;
    border: none;
  }

  .group-copy-datepicker .react-datepicker__day--outside-month,
  .group-range-datepicker .react-datepicker__day--outside-month {
    opacity: 0.2;
    pointer-events: none;
  }
`;

const DatePickerCopyGroupWrapper = styled.section``;
const DatePickerGroupWrapper = styled.section``;

// helper for link url //
const isValidUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

function LocationGroupListItem(props) {
  const classes = useStyles();

  const [groupHex, setGroupHex] = useState(props.groupColor);
  const [anchorElColor, setAnchorElColor] = useState(null);
  const [anchorElDateRange, setAnchorElDateRange] = useState(null);
  const [colorOpen, setColorOpen] = useState(false);
  const colorId = colorOpen ? "color-popover" : undefined;
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [opened, setOpened] = useState(true);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCopyOpen, setConfirmCopyOpen] = useState(false);
  const [editGroupNameOpen, setEditGroupNameOpen] = useState(false);
  const [addSpecialInstructionsOpen, setAddSpecialInstructionsOpen] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState(props.specialInstructions);
  const [addLostCallNumberOpen, setAddLostCallNumberOpen] = useState(false);
  const [addWebsiteLinkOpen, setAddWebsiteLinkOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState(props.websiteUrl || "");
  const [websiteLinkText, setWebsiteLinkText] = useState(props.websiteLinkText || "");
  const [lostCallNumber, setLostCallNumber] = useState(props.lostCallNumber);
  const [lostCallName, setLostCallName] = useState(props.lostCallName);
  const [pending, setPending] = useState(false);
  
  const theme = useTheme();
  const ifSmall = useMediaQuery(theme.breakpoints.down("xs"));

  const [copyDayKey, setCopyDayKey] = useState(null); // ✅ dayKey instead of toDateString
  const copyDayLabel = copyDayKey ? dayKeyToLocalDate(copyDayKey).toDateString() : null;
  const [locs, setLocs] = useState(null);

  // ✅ NEW provider API
  const { selectedDate, setSelectedDate, dayKey: selectedDayKey, dayLabel } = useContext(DateContext);
  const { category } = useContext(CategoryContext);

  // Prefer new dayKey fields on group; fallback to legacy ms fields if needed
  const groupStartDayKey = props.startDay ?? (props.startDate != null ? toDayKey(new Date(props.startDate)) : null);
  const groupEndDayKey = props.endDay ?? (props.endDate != null ? toDayKey(new Date(props.endDate)) : groupStartDayKey);

  const groupStartDateObj = groupStartDayKey ? dayKeyToLocalDate(groupStartDayKey) : startOfLocalDay(new Date());
  const groupEndDateObj = groupEndDayKey ? dayKeyToLocalDate(groupEndDayKey) : startOfLocalDay(new Date());

  // Popover range state should be Date objects (react-datepicker wants Date)
  const [newStartDate, setNewStartDate] = useState(groupStartDateObj);
  const [newEndDate, setNewEndDate] = useState(groupEndDateObj);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangePickerId = showDateRangePicker ? "dateRangePicker-popover" : undefined;

  const unchanged =
  toDayKey(newStartDate) === groupStartDayKey &&
  toDayKey(newEndDate) === groupEndDayKey;

  // display strings
  const formattedStartDate = formatMMDD(groupStartDateObj, ifSmall);
  const formattedEndDate = formatMMDD(groupEndDateObj, ifSmall);

  // If single day range, show that day
  const formattedRangeLabel =
    groupStartDayKey && groupEndDayKey && groupStartDayKey !== groupEndDayKey
      ? `${formattedStartDate} - ${formattedEndDate}`
      : formatMMDD(groupStartDateObj, ifSmall);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: props.groupId,
    animateLayoutChanges: () => false,
  });

  const sortableStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const { handleSubmit, register, errors } = useForm();


const handleUrlChange = (e) => {
  let value = e.target.value.trim();

  if (!value) {
    setWebsiteUrl(value);
    return;
  }

  // If already has http:// or https:// → leave it alone
  if (/^https?:\/\//i.test(value)) {
    setWebsiteUrl(value);
    return;
  }

  // Otherwise prepend https://
  setWebsiteUrl(`https://${value}`);
};

  // ----- menu / color -----
  const handleClickColor = (event) => {
    setGroupHex(props.groupColor);
    setAnchorElColor(event.currentTarget);
    setColorOpen(true);
  };

  const handleCloseColor = () => {
    setAnchorElColor(null);
    setColorOpen(false);
  };

  const handleSaveColor = (color) => {
    updateLocationGroup(props.groupId, { groupColor: color })
      .then((response) => {
        response.success
          ? toast.success(`Successfully updated color code for location group "${props.groupName}".`)
          : toast.error(`Failed to update the color code for location group "${props.groupName}".`);
      })
      .catch(() => toast.error(`Failed to update the color code for location group "${props.groupName}".`));

    handleCloseColor();
  };

  const handleClickDateRange = (event) => {
    setAnchorElDateRange(event.currentTarget);
    setShowDateRangePicker((v) => !v);
  };

  const handleCloseDateRange = () => {
    setAnchorElDateRange(null);
    setShowDateRangePicker(false);
    setNewStartDate(groupStartDateObj);
    setNewEndDate(groupEndDateObj);
  };

  const updateStartEndDates = () => {
    if (!newStartDate || !newEndDate) return;

    const startDay = toDayKey(startOfLocalDay(newStartDate));
    const endDay = toDayKey(startOfLocalDay(newEndDate));

    // ✅ Write dayKey fields (timezone-proof)
    // ⛑️ Optional: keep legacy ms during migration (remove later)
    const legacyStartMs = startOfLocalDay(newStartDate).getTime();
    const legacyEndMs = startOfLocalDay(newEndDate).getTime();

    updateLocationGroup(props.groupId, {
      startDay,
      endDay,
      startDate: legacyStartMs, // optional migration support
      endDate: legacyEndMs,     // optional migration support
    })
      .then((response) => {
        response.success
          ? toast.success(`Successfully updated the date range for location group "${props.groupName}".`)
          : toast.error(`Failed to update the date range for location group "${props.groupName}".`);
      })
      .catch(() => toast.error(`Failed to update the date range for location group "${props.groupName}".`));

    handleCloseDateRange();
  };

  const collapseGroup = () => setOpened((v) => !v);

  const handleClickGroupActionMenu = (event) => setAnchorElMenu(event.currentTarget);
  const handleCloseGroupActionMenu = () => setAnchorElMenu(null);

  const handleCloseConfirm = () => {
    setConfirmDeleteOpen(false);
    setConfirmCopyOpen(false);
    handleCloseEditGroupName();
    setCopyDayKey(null);
  };

  const handleDeleteAndClose = () => {
    setConfirmDeleteOpen(false);
    deleteLocationGroup(props.groupId)
      .then((response) => {
        if (response.success) {
          deleteLocationsByGroup(props.groupId, props.productionId);
          toast.success(response.message);
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => toast.error(error.message));
    handleCloseEditGroupName();
  };

  // keep opened on day/category change
  useEffect(() => {
    setOpened(true);
  }, [selectedDayKey, category]);

  // when props change, refresh datepicker state
  useEffect(() => {
    setNewStartDate(groupStartDateObj);
    setNewEndDate(groupEndDateObj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.startDay, props.endDay, props.startDate, props.endDate]);

  useEffect(() => {
    setLostCallName(props.lostCallName);
    setLostCallNumber(props.lostCallNumber);
    setSpecialInstructions(props.specialInstructions);
  }, [props.lostCallName, props.lostCallNumber, props.specialInstructions]);

  // ----- copy group -----
  const handleCopy = async () => {
    if (!copyDayKey) return;

    const copyDateObj = dayKeyToLocalDate(copyDayKey);

    const theName =
      selectedDayKey === copyDayKey
        ? props.groupName.includes("copy)")
          ? props.groupName
          : props.groupName + " (copy)"
        : props.groupName;

    const data = {
      productionId: props.productionId,
      productionName: props.productionName,
      name: theName,
      groupColor: props.groupColor,
      category,

      // ✅ dayKey storage
      startDay: copyDayKey,
      endDay: copyDayKey,

      // ⛑️ optional legacy during migration
      startDate: copyDateObj.getTime(),
      endDate: copyDateObj.getTime(),

      ...(props.specialInstructions && { specialInstructions: props.specialInstructions }),
      ...(props.lostCallName && { lostCallName: props.lostCallName }),
      ...(props.lostCallNumber && { lostCallNumber: props.lostCallNumber }),
      customTypes: props.groupCustomTypes ?? [],
      sortOrder: props.sortOrder || 0,
    };

    const updatedLocs = (locs ?? []).map((loc) => ({
      ...loc,
      createdAt: Timestamp.now(),
      productionName: props.productionName,
    }));

    handleCloseGroupActionMenu();
    setConfirmCopyOpen(false);

    try {
      const response = await copyLocationGroup(data, updatedLocs);
      setPending(false);

      if (response.success) {
        setLostCallName("");
        setLostCallNumber("");
        setSelectedDate(copyDateObj); // ✅ provider normalizes
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (e) {
      toast.error(e?.message ?? "Failed to copy group.");
    }
  };

  const handleCopyProdCat = async () => {
    // Production category copy uses existing range
    const theName = props.groupName.includes("copy)") ? props.groupName : props.groupName + " (copy)";

    const data = {
      productionId: props.productionId,
      productionName: props.productionName,
      name: theName,
      groupColor: props.groupColor,
      category,

      // Prefer dayKey fields if present
      ...(groupStartDayKey ? { startDay: groupStartDayKey } : {}),
      ...(groupEndDayKey ? { endDay: groupEndDayKey } : {}),

      // ⛑️ optional legacy
      ...(props.startDate != null ? { startDate: props.startDate } : {}),
      ...(props.endDate != null ? { endDate: props.endDate } : {}),

      ...(props.specialInstructions && { specialInstructions: props.specialInstructions }),
      ...(props.lostCallName && { lostCallName: props.lostCallName }),
      ...(props.lostCallNumber && { lostCallNumber: props.lostCallNumber }),
      customTypes: props.groupCustomTypes ?? [],
      sortOrder: props.sortOrder || 0,
    };

    const updatedLocs = (locs ?? []).map((loc) => ({
      ...loc,
      createdAt: Timestamp.now(),
      productionName: props.productionName,
    }));

    await copyLocationGroup(data, updatedLocs);
    setConfirmCopyOpen(false);
    handleCloseGroupActionMenu();
    setCopyDayKey(null);
  };

  // ----- dialogs close helpers -----
  const handleCloseEditGroupName = () => {
    setEditGroupNameOpen(false);
    handleCloseGroupActionMenu();
  };

  const handleCloseAddLostCallNumber = () => {
    setAddLostCallNumberOpen(false);
    handleCloseGroupActionMenu();
  };

  const handleCloseAddSpecialInstructions = () => {
    setAddSpecialInstructionsOpen(false);
    handleCloseGroupActionMenu();
  };

    const handleCloseAddWebsiteLink = () => {
    setAddWebsiteLinkOpen(false);
    handleCloseGroupActionMenu();
  };

  // ----- delete phone / instructions -----
  const handleDeleteLostCallNumber = () => {
    deletePhoneFromLocationGroup(props.groupId)
      .then((response) => {
        handleCloseAddLostCallNumber();
        setPending(false);
        if (response.success) {
          setLostCallName("");
          setLostCallNumber("");
          toast.success("Successfully deleted contact info.");
        } else {
          toast.error("Failed to delete contact info. Please try again later.");
        }
      })
      .catch((error) => {
        setPending(false);
        toast.error(error.message);
      });
  };

  const handleDeleteSpecialInstructions = () => {
    deleteSpecialInstructionsFromLocationGroup(props.groupId)
      .then((response) => {
        handleCloseAddSpecialInstructions();
        setPending(false);
        if (response.success) {
          setSpecialInstructions("");
          toast.success("Successfully deleted special instructions.");
        } else {
          toast.error("Failed to delete special instructions. Please try again later.");
        }
      })
      .catch((error) => {
        setPending(false);
        toast.error(error.message);
      });
  };

  const handleDeleteWebsiteLink = () => {
  setPending(true);

  deleteWebsiteLinkFromLocationGroup(props.groupId)
    .then((response) => {
      handleCloseAddWebsiteLink();
      setPending(false);

      if (response.success) {
        setWebsiteUrl("");
        setWebsiteLinkText("");
        toast.success("Successfully deleted website link.");
      } else {
        toast.error("Failed to delete website link. Please try again later.");
      }
    })
    .catch((error) => {
      setPending(false);
      toast.error(error.message);
    });
};

  // react-datepicker range handler
  const onChange = (dates) => {
    const [start, end] = dates;
    setNewStartDate(start);
    setNewEndDate(end);
  };

  const handleChangeGroupName = (data) => {
    handleCloseGroupActionMenu();
    setPending(true);

    const query = props.groupId && data.newGroupName
      ? updateLocationGroup(props.groupId, { name: data.newGroupName.toUpperCase() })
      : null;

    if (!query) return;

    query
      .then((response) => {
        handleCloseEditGroupName();
        setPending(false);
        response.success
          ? toast.success("Group name has been successfully updated.")
          : toast.error("Failed to update group name. Please try again later.");
      })
      .catch((error) => {
        setPending(false);
        toast.error(error.message);
      });
  };

  const handleAddLostCallNumber = () => {
    setPending(true);
    const query =
      props.groupId && lostCallNumber && lostCallName
        ? updateLocationGroup(props.groupId, { lostCallNumber, lostCallName: lostCallName.toUpperCase() })
        : null;

    if (!query) return;

    query
      .then((response) => {
        handleCloseAddLostCallNumber();
        setPending(false);
        response.success
          ? toast.success("Contact info has been successfully updated.")
          : toast.error("Failed to update contact info. Please try again later.");
      })
      .catch((error) => {
        setPending(false);
        toast.error(error.message);
      });
  };

  const handleAddSpecialInstructions = () => {
    setPending(true);
    const query =
      props.groupId && specialInstructions
        ? updateLocationGroup(props.groupId, { specialInstructions: specialInstructions.toUpperCase() })
        : null;

    if (!query) return;

    query
      .then((response) => {
        handleCloseAddSpecialInstructions();
        setPending(false);
        response.success
          ? toast.success("Special instructions has been successfully updated.")
          : toast.error("Failed to update special instructions. Please try again later.");
      })
      .catch((error) => {
        setPending(false);
        toast.error(error.message);
      });
  };

  const handleAddWebsiteLink = () => {
  setPending(true);

  // optional: normalize URL (prepend https if missing)
  const normalizedUrl =
    websiteUrl && !/^https?:\/\//i.test(websiteUrl)
      ? `https://${websiteUrl}`
      : websiteUrl;

  if (!isValidUrl(normalizedUrl)) {
    setPending(false);
    toast.error("Please enter a valid URL.");
    return;
  }

  const query =
    props.groupId && normalizedUrl && websiteLinkText
      ? updateLocationGroup(props.groupId, {
          websiteUrl: normalizedUrl,
          websiteLinkText: websiteLinkText.toUpperCase()
        })
      : null;

  if (!query) {
    setPending(false);
    return;
  }

  query
    .then((response) => {
      handleCloseAddWebsiteLink();
      setPending(false);

      response.success
        ? toast.success("Website link has been successfully updated.")
        : toast.error("Failed to update website link. Please try again later.");
    })
    .catch((error) => {
      setPending(false);
      toast.error(error.message);
    });
};

  const CopyGroupDatePickerInput = forwardRef(({ onClick }, ref) => (
    <div onClick={onClick} ref={ref}>
      <MenuItem>
        <ListItemIcon>
          <FileCopyIcon fontSize="medium" />
        </ListItemIcon>
        Copy Group
      </MenuItem>
    </div>
  ));

  return (
    <>
      <DatePickerStyles />
      <div ref={setNodeRef} style={sortableStyle}>
        <Paper style={colorOpen ? { backgroundColor: groupHex } : { backgroundColor: props.groupColor }} className={classes.groupContainer}>
          <Box px={2} className={classes.groupBoxHeader}>
            <div style={{ cursor: "pointer" }} onClick={collapseGroup}>
              {opened ? <ExpandMoreIcon style={{ verticalAlign: "middle" }} /> : <ExpandLessIcon style={{ verticalAlign: "middle" }} />}
            </div>

            <div>
              <span style={{ cursor: "pointer" }} onClick={collapseGroup}>{props.groupName}</span>
              {props.auth.user.planIsActive && (
                <MoreHoriz
                  aria-haspopup="true"
                  onClick={handleClickGroupActionMenu}
                  style={{ cursor: "pointer", verticalAlign: "middle", width: 40 }}
                />
              )}
            </div>

            <Menu
              id="simple-menu"
              anchorEl={anchorElMenu}
              keepMounted
              open={Boolean(anchorElMenu)}
              onClose={handleCloseGroupActionMenu}
            >
              <MenuItem onClick={() => setEditGroupNameOpen(true)}>
                <ListItemIcon><EditIcon fontSize="medium" /></ListItemIcon>
                Edit Group Name
              </MenuItem>

              {category !== "production" ? (
                <DatePickerCopyGroupWrapper>
                  <DatePicker
                    selected={selectedDate}
                    allowSameDay
                    onChange={(date) => {
                      if (!date) return;
                      setConfirmCopyOpen(true);
                      setCopyDayKey(toDayKey(startOfLocalDay(date))); // ✅ store dayKey
                    }}
                    customInput={<CopyGroupDatePickerInput />}
                    todayButton="Today"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    withPortal
                    calendarClassName="group-copy-datepicker"
                    popperClassName="group-copy-popper"
                  >
                    <div style={{ position: "absolute", top: "10px", left: "10px", color: "white", width: "90%" }}>
                      SELECT A DATE TO COPY THE GROUP TO
                    </div>
                  </DatePicker>
                </DatePickerCopyGroupWrapper>
              ) : (
                <MenuItem onClick={() => setConfirmCopyOpen(true)}>
                  <ListItemIcon><FileCopyIcon fontSize="medium" /></ListItemIcon>
                  Copy Group
                </MenuItem>
              )}

              <MenuItem onClick={() => setConfirmDeleteOpen(true)}>
                <ListItemIcon><DeleteIcon fontSize="medium" /></ListItemIcon>
                Delete Group
              </MenuItem>

              <MenuItem onClick={() => setAddSpecialInstructionsOpen(true)}>
                <ListItemIcon><NotesIcon fontSize="medium" /></ListItemIcon>
                Add/Edit Special Instructions
              </MenuItem>

              <MenuItem onClick={() => setAddLostCallNumberOpen(true)}>
                <ListItemIcon><AddIcCallIcon fontSize="medium" /></ListItemIcon>
                Add/Edit Locations Rep Contact
              </MenuItem>

              <MenuItem onClick={() => setAddWebsiteLinkOpen(true)}>
                <ListItemIcon><InsertLinkIcon fontSize="medium" /></ListItemIcon>
                Add/Edit Website Link
              </MenuItem>

            </Menu>

            {props.auth.user.planIsActive && (
              <div style={{ display: "flex", marginLeft: "auto", alignItems: "center" }}>
                {category !== "production" && (
                  <div
                    onClick={handleClickDateRange}
                    style={ifSmall ? { paddingRight: "10px", cursor: "pointer", fontSize: "1em", textAlign: "center", minWidth: 110 } : { paddingRight: "10px", cursor: "pointer" }}
                  >
                    {formattedRangeLabel}
                  </div>
                )}

                <div>
                  <img
                    onClick={handleClickColor}
                    style={{ verticalAlign: "middle", height: 24, cursor: "pointer", float: "right" }}
                    src={"/colorWheelIcon.png"}
                    alt="color wheel icon for picker"
                  />
                </div>

                {props.numberOfGroupsInList > 1 && (
                  <div {...attributes} {...listeners}>
                    {((!ifSmall) || (ifSmall && !opened)) && (
                      <DragIndicatorIcon style={{ verticalAlign: "middle", cursor: "grab" }} />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* DATE RANGE PICKER */}
            <Popover
              id={dateRangePickerId + "-" + props.groupId}
              open={showDateRangePicker}
              anchorEl={anchorElDateRange}
              onClose={handleCloseDateRange}
              disableAutoFocus
              disableEnforceFocus
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Fragment>
                <div>
                  <DatePickerGroupWrapper>
                    <DatePicker
                      onChange={onChange}
                      startDate={newStartDate}
                      endDate={newEndDate}
                      selectsRange
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      inline
                      calendarClassName="group-range-datepicker"
                    />
                  </DatePickerGroupWrapper>
                </div>

                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  onClick={updateStartEndDates}
                  disabled={!newStartDate || !newEndDate || unchanged}
                >
                  Save
                </Button>
              </Fragment>
            </Popover>
          </Box>

          <Collapse isOpened={opened}>
            <LocationList
              groupId={props.groupId}
              groupName={props.groupName}
              groupColor={props.groupColor}
              specialInstructions={props.specialInstructions}
              lostCallNumber={props.lostCallNumber}
              lostCallName={props.lostCallName}
              websiteLinkText={props.websiteLinkText}
              websiteUrl={props.websiteUrl}
              groupCustomTypes={props.groupCustomTypes}
              productionId={props.productionId}
              setLocs={setLocs}
              listOfGroupsAndDays={props.listOfGroupsAndDays}
            />
          </Collapse>
        </Paper>
      </div>

      {/* COLOR WHEEL POPOVER */}
      <Popover
        id={colorId}
        open={colorOpen}
        anchorEl={anchorElColor}
        onClose={handleCloseColor}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Fragment>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "280px" }}>
            <div style={{ paddingLeft: 26, paddingTop: 10 }}>
              <Circle
                colors={[
                  "#f44336","#e91e63","#9c27b0","#673ab7","#3f51b5",
                  "#2196f3","#03a9f4","#00bcd4","#009688","#4caf50",
                  "#8bc34a","#ffc107","#ff9800","#ff5722","#795548","#607d8b"
                ]}
                color={groupHex}
                onChange={(color) => setGroupHex(color.hex)}
              />
            </div>
          </div>

          <Button variant="contained" fullWidth color="primary" onClick={() => handleSaveColor(groupHex)}>
            Save
          </Button>
        </Fragment>
      </Popover>

      {/* DELETE GROUP DIALOG */}
      <Dialog open={confirmDeleteOpen} onClose={handleCloseConfirm} style={{ textAlign: "center" }}>
        <DialogTitle>Are you sure you want to delete "{props.groupName}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div>THIS WILL DELETE ALL LOCATIONS IN THE GROUP.</div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleCloseConfirm} color="primary">Cancel</Button>
          <Button onClick={handleDeleteAndClose} color="primary" autoFocus>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* COPY GROUP DIALOG */}
      <Dialog open={confirmCopyOpen} onClose={handleCloseConfirm} style={{ textAlign: "center" }}>
        {category !== "production" ? (
          <DialogTitle>
            Are you sure you want to copy "{props.groupName}" to {copyDayLabel ?? "that day"}?
          </DialogTitle>
        ) : (
          <DialogTitle>Are you sure you want to copy "{props.groupName}" ?</DialogTitle>
        )}
        <DialogActions>
          <Button autoFocus onClick={handleCloseConfirm} color="primary">Cancel</Button>
          <Button onClick={category === "production" ? handleCopyProdCat : handleCopy} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* CHANGE GROUP NAME DIALOG */}
      <Dialog open={editGroupNameOpen} onClose={handleCloseEditGroupName}>
        <form onSubmit={handleSubmit(handleChangeGroupName)}>
          <DialogTitle>Edit Group Name:</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Grid item>
                <TextField
                  autoComplete="off"
                  variant="outlined"
                  type="text"
                  label="Group Name"
                  name="newGroupName"
                  defaultValue={props.groupName}
                  error={!!errors.newGroupName}
                  helperText={errors.newGroupName && errors.newGroupName.message}
                  fullWidth
                  autoFocus
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  inputRef={register({ required: "Group name cannot be empty" })}
                />
                ie: MAIN UNIT, 2ND UNIT, DAY 1 OF 20, CITY, ETC…
              </Grid>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditGroupName} color="primary">Cancel</Button>
            <Button variant="contained" color="primary" size="large" type="submit" disabled={pending}>
              {!pending ? <span>Save</span> : <CircularProgress size={28} />}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ADD SPECIAL INSTRUCTIONS DIALOG */}
      <Dialog open={addSpecialInstructionsOpen} onClose={handleCloseAddSpecialInstructions}
        maxWidth="sm"
        PaperProps={{
          style: {
            width: 500,
            maxWidth: "500px"
          }
        }}
      >
        <form onSubmit={handleSubmit(handleAddSpecialInstructions)}>
          <DialogTitle>Add/Edit Special Instructions:</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Grid item>
                <TextField
                  style={{ paddingBottom: "10px" }}
                  autoComplete="off"
                  variant="outlined"
                  type="text"
                  label="Special Instructions"
                  name="specialInstructions"
                  defaultValue={props.specialInstructions}
                  fullWidth
                  autoFocus
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  onChange={(e) => setSpecialInstructions(e.target?.value)}
                />
              </Grid>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddSpecialInstructions} color="primary">Cancel</Button>
            {props.specialInstructions && (
              <Button variant="contained" color="secondary" size="large" onClick={handleDeleteSpecialInstructions}>
                Delete
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              disabled={
                pending ||
                !specialInstructions ||
                specialInstructions.toString().length < 1 ||
                specialInstructions === props.specialInstructions
              }
            >
              {!pending ? <span>Save</span> : <CircularProgress size={28} />}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ADD LOST CALL NUMBER DIALOG */}
      <Dialog open={addLostCallNumberOpen} onClose={handleCloseAddLostCallNumber} maxWidth="sm"
    PaperProps={{
      style: {
        width: 500,
        maxWidth: "500px"
      }
    }}>
        <form onSubmit={handleSubmit(handleAddLostCallNumber)}>
          <DialogTitle>Add/Edit Locations Rep Contact:</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Grid item>
                <TextField
                  style={{ paddingBottom: "10px" }}
                  autoComplete="off"
                  variant="outlined"
                  type="text"
                  label="Name"
                  name="lostCallName"
                  defaultValue={props.lostCallName}
                  fullWidth
                  autoFocus
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  onChange={(e) => setLostCallName(e.target?.value)}
                />
                <PhoneInput
                  country="us"
                  onlyCountries={["us"]}
                  label="Contact Number"
                  name="lostCallNumber"
                  value={props.lostCallNumber}
                  onChange={(phone) => setLostCallNumber(phone)}
                />
              </Grid>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddLostCallNumber} color="primary">Cancel</Button>
            {props.lostCallNumber && (
              <Button variant="contained" color="secondary" size="large" onClick={handleDeleteLostCallNumber}>
                Delete
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              disabled={
                pending ||
                !lostCallNumber ||
                isNaN(lostCallNumber) ||
                lostCallNumber.toString().length < 11 ||
                !lostCallName ||
                (lostCallNumber === props.lostCallNumber && lostCallName === props.lostCallName)
              }
            >
              {!pending ? <span>Save</span> : <CircularProgress size={28} />}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

  {/* ADD / EDIT WEBSITE LINK DIALOG */}
  <Dialog
    open={addWebsiteLinkOpen}
    onClose={handleCloseAddWebsiteLink}
    fullWidth
    maxWidth="sm"
    PaperProps={{
      style: {
        width: 500,
        maxWidth: "500px"
      }
    }}
  >
    
    <form onSubmit={handleSubmit(handleAddWebsiteLink)}>
      <DialogTitle>Add/Edit Website Link:</DialogTitle>

      <DialogContent>
        <Grid item>
          <TextField
            margin="dense"
            autoComplete="off"
            variant="outlined"
            type="text"
            label="URL"
            name="websiteUrl"
            fullWidth
            autoFocus
            value={websiteUrl}
            onChange={handleUrlChange}
            error={!!websiteUrl && !isValidUrl(websiteUrl)}
            helperText={
              !!websiteUrl && !isValidUrl(websiteUrl)
                ? "Enter a valid URL (example: https://example.com)"
                : " "
            }
            FormHelperTextProps={{
              style: { minHeight: 20 }
            }}
          />

          <TextField
            margin="dense"
            autoComplete="off"
            variant="outlined"
            type="text"
            label="NAME / DESCRIPTION"
            name="websiteLinkText"
            inputProps={{ style: { textTransform: "uppercase" } }}
            fullWidth
            value={websiteLinkText}
            onChange={(e) => setWebsiteLinkText(e.target.value)}
            helperText=" "
            FormHelperTextProps={{
              style: { minHeight: 20 }
            }}
          />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseAddWebsiteLink} color="primary">
          Cancel
        </Button>

        {props.websiteUrl && (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleDeleteWebsiteLink}
          >
            Delete
          </Button>
        )}

        <Button
          variant="contained"
          color="primary"
          size="large"
          type="submit"
          disabled={
            pending ||
            !websiteUrl ||
            !isValidUrl(websiteUrl) ||
            !websiteLinkText ||
            (websiteUrl === props.websiteUrl &&
              websiteLinkText === props.websiteLinkText)
          }
        >
          {!pending ? <span>Save</span> : <CircularProgress size={28} />}
        </Button>
      </DialogActions>
    </form>
  </Dialog>

    </>
  );
}

export default LocationGroupListItem;